<script setup lang="ts">
/**
 * The menu panel. Portalled to `<body>` and positioned by reka's popper, which flips and shifts it
 * to stay inside the viewport (`avoidCollisions`, 8px from the edge) — the job every hand-rolled menu
 * used to do with its own `Math.min(x, innerWidth - W)` clamp. Pass `reference` (an object with a
 * `getBoundingClientRect`) and no trigger to anchor it to a point, which is how a right-click menu
 * opens where the pointer is.
 */
import type { DropdownMenuContentEmits, DropdownMenuContentProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { reactiveOmit } from "@vueuse/core"
import {
  DropdownMenuContent,
  DropdownMenuPortal,
  useForwardPropsEmits,
} from "reka-ui"
import { cn } from "@/lib/utils"

// The root element is the portal, so attributes (`data-testid`, `aria-label`, …) are bound onto the
// panel explicitly instead of falling through to nowhere.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<DropdownMenuContentProps & { class?: HTMLAttributes["class"] }>(),
  { sideOffset: 4, collisionPadding: 8 },
)
const emits = defineEmits<DropdownMenuContentEmits>()

const delegatedProps = reactiveOmit(props, "class")

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DropdownMenuPortal>
    <DropdownMenuContent
      v-bind="{ ...forwarded, ...$attrs }"
      :class="cn(
        'z-50 min-w-[8rem] max-h-[var(--reka-dropdown-menu-content-available-height)] overflow-y-auto overflow-x-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 motion-reduce:animate-none',
        props.class,
      )"
    >
      <slot />
    </DropdownMenuContent>
  </DropdownMenuPortal>
</template>
